<template>
  <div>
    <fetching-data-spinner
      :error="fetchError"
      :loading="fetch"
      :loading-title="$t('reply.form.loading-reply')"
      enable-retry
      @retry="fetchReply"
    />
    <b-card v-if="!fetch">
      <error-alert
        v-if="error"
        :error="error"
      />
      <b-row>
        <template v-if="replyTemplates.length && !edit && !isTemplate">
          <b-col
            md="6"
            sm="12"
            class="mb-1"
          >
            <b-form-checkbox
              v-model="fromSaved"
              switch
            >
              {{ $t('reply.form.choose_saved_template') }}
            </b-form-checkbox>
            <div
              class="ml-2"
            >
              <general-templates-select
                v-if="fromSaved"
                v-model="templateId"
                :label="$t('reply.form.choose-reply-template')"
                :clearable="true"
                :options="replyTemplates"
              />
            </div>
          </b-col>
          <b-col md="12" />
        </template>
        <b-col
          md="6"
          sm="12"
        >
          <ok-text-input
            v-model="form.name"
            :label="$t('reply.template_name')"
            is-required
          />
        </b-col>
        <b-col md="12" />
        <b-col
          md="6"
          sm="12"
          class="mt-2"
        >
          <b-form-checkbox
            v-model="form.comment_enabled"
            switch
          >
            {{ $t('reply.form.enable_comment_reply') }}
          </b-form-checkbox>
          <template v-if="form.comment_enabled">
            <div
              v-if="isCommentAlogEnabled && defaultAccounts.length"
              class="my-2"
            >
              <general-templates-select
                v-model="form.account_id"
                :label="$t('reply.form.choose-comment-account')"
                :clearable="true"
                :options="defaultAccounts"
                label-key="account_name"
              />
            </div>
            <div
              v-if="commentTemplates.length"
              class="ml-2"
            >
              <general-templates-select
                v-model="form.comment_template_id"
                :label="$t('reply.form.choose-comment-template')"
                :clearable="true"
                :placeholder="$t('reply.form.comment_by_app')"
                :options="commentTemplates"
                label-key="title"
              />
            </div>
            <div
              v-if="!isTemplate && pageCanUseApi"
              class="my-2 p-2"
              style="border: 1px dashed #6f42c1; border-radius: 6px"
            >
              <b-form-checkbox
                v-model="form.ai_enabled"
                switch
              >
                {{ $t('reply.form.ai_comment_reply') }}
              </b-form-checkbox>
              <h6
                style="font-size: 12px"
                class="text-muted mt-1"
              >
                {{ $t('reply.form.ai_comment_reply_hint') }}
              </h6>
              <p
                v-if="Number(currentPage.ai_reply_limit) > 0"
                class="text-white small mb-1 bg-info p-2 rounded"
              >
                {{ $t('reply.form.ai_pool_status', { used: Number(currentPage.ai_comment_replies_used) || 0, cap: Number(currentPage.ai_reply_limit) }) }}
              </p>
              <div class="mt-2">
                <label
                  class="d-block small font-weight-bold"
                  for="fb-reply-ai-limit"
                >{{ $t('reply.form.ai_limit') }}</label>
                <b-form-spinbutton
                  id="fb-reply-ai-limit"
                  v-model.number="form.ai_limit"
                  class="mt-1"
                  style="max-width: 220px"
                  min="0"
                  :max="maxAiLimitForReply"
                />
                <h6
                  style="font-size: 12px"
                  class="text-muted mt-1"
                >
                  {{ $t('reply.form.ai_limit_hint') }}
                </h6>
              </div>
              <ok-text-input
                v-model="form.ai_post_description"
                class="mt-2"
                :label="$t('reply.form.ai_post_description')"
                textarea
                :rows="4"
              />
              <h6
                style="font-size: 12px"
                class="text-warning mt-1"
              >
                {{ $t('reply.form.ai_post_description_hint') }}
              </h6>
            </div>
            <div
              v-if="maxComments>1"
              class="comment-number-container"
            >
              <label
                for="comment-num"
                class="flex-grow-1"
              >

                {{ $t('reply.form.comment-number') }}  <FeatherIcon
                  v-b-tooltip.hover
                  icon="AlertOctagonIcon"
                  :title="$t('reply.form.comment-number-explain')"
                /> :</label>
              <b-form-spinbutton
                id="comment-num"
                v-model.number="form.comment_number"
                class="ml-sm-1 ml-md-3 flex-grow-0"
                style="max-width: 293px"
                min="1"
                :max="maxComments"
              />
              <h6
                style="font-size: 12px"
                class="text-warning mt-1"
              >
                {{ $t('reply.form.comment-number-note') }}
              </h6>
              <div
                v-if="isCommentAlogEnabled && multiAccounts.length"
                class="my-2"
              >
                <general-templates-select
                  v-model="form.multi_account_id"
                  :label="$t('reply.form.choose-comment-account')"
                  :clearable="true"
                  :options="multiAccounts"
                  label-key="account_name"
                />
              </div>
            </div>
            <div class="mt-1">
              <b-form-checkbox
                v-model="form.enable_multiple"
                switch
              >
                {{ $t('reply.reply_multiple_time') }}
              </b-form-checkbox>
            </div>
          </template>
          <div class="mt-1">
            <b-form-checkbox
              v-model="form.auto_like"
              switch
            >
              {{ $t('reply.form.auto_like') }}
            </b-form-checkbox>
            <h6
              style="font-size: 12px"
              class="text-warning"
            >
              {{ $t('reply.form.comment-number-note') }}
            </h6>
          </div>
          <div
            v-if="form.auto_like || form.comment_enabled"
            class="comment-number-container mt-1"
          >
            <label
              for="comment-num"
              class="flex-grow-1"
            >

              {{ $t('reply.form.comment-delay') }} :</label>
            <b-form-spinbutton
              id="comment-num"
              v-model.number="form.delay"
              class="ml-sm-1 ml-md-3 flex-grow-0"
              style="max-width: 293px"
              :min="0"
              :max="120"
            />
          </div>
          <b-form-checkbox
            v-model="form.message_enabled"
            class="mt-2"
            switch
          >
            {{ $t('reply.form.enable_message_reply') }}
          </b-form-checkbox>
        </b-col>
        <template v-if="form.message_enabled">
          <b-col
            md="12"
            class="mt-2"
          >
            <b-form-checkbox
              v-model="form.include_url"
              class="mt-2"
              switch
            >
              {{ $t('reply.form.include_url') }}
            </b-form-checkbox>
          </b-col>
          <b-col
            md="12"
            class="mt-2"
          >
            <Types
              ref="replyTypes"
              :tab-i="typTabIndex"
              :change-type-reply="changeTypeReply"
            />
          </b-col>
        </template>

        <b-col
          v-if="!isTemplate"
          md="12"
          class="mt-2"
        >
          <hr>
          <b-form-checkbox
            v-if="!edit"
            v-model="form.enable_old"
            class="mt-2"
            switch
          >
            {{ $t('reply.reply_old_hint') }}
          </b-form-checkbox>
          <b-form-checkbox
            v-if="!edit"
            v-model="save"
            class="mt-2"
            switch
          >
            {{ $t('reply.form.save_template') }}
          </b-form-checkbox>
          <b-form-checkbox
            v-model="schedule"
            class="mt-2"
            switch
          >
            {{ $t('reply.form.stop_at') }}
          </b-form-checkbox>
          <reply-schedule
            v-if="schedule"
            ref="schedule"
          />
        </b-col>
      </b-row>
      <hr>
      <div class="d-flex justify-content-center">
        <b-button
          :disabled="loading"
          variant="success"
          @click="submitReply"
        >
          <b-spinner
            v-if="loading"
            small
            class="ml-1"
          />
          {{ $t('forms.save') }}
        </b-button>
      </div>
    </b-card>
  </div>

</template>

<script>
import { mapGetters } from 'vuex'
import {
  BCard, BRow, BCol, BFormCheckbox, BButton, BSpinner, BFormSpinbutton, VBTooltip,
} from 'bootstrap-vue'
import OkTextInput from '@/@bya3/components/inputs/OkTextInput'
import ErrorAlert from '@/@bya3/components/alerts/ErrorAlert'
import ReplyApi from '@/api/reply'
import FormsService from '@/services/forms-service'
import GeneralTemplatesSelect from '@/@bya3/components/inputs/GeneralTemplatesSelect'
import ReplySchedule from '@/views/owner-fb/reply/components/ReplySchedule'
import FetchingDataSpinner from '@/@bya3/components/utils/FetchingDataSpinner'
import Types from './templates/ReplyTypes'

export default {
  name: 'ProcessReply',
  components: {
    ReplySchedule,
    GeneralTemplatesSelect,
    FetchingDataSpinner,
    ErrorAlert,
    OkTextInput,
    Types,
    BCard,
    BRow,
    BCol,
    BFormCheckbox,
    BButton,
    BSpinner,
    BFormSpinbutton,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      changeTypeReply: false,
      error: null,
      fromSaved: false,
      templateId: null,
      schedule: false,
      fetch: true,
      typTabIndex: 0,
      fetchError: null,
      save: false,
    }
  },
  computed: {
    ...mapGetters({
      form: 'reply/form',
      pageId: 'pages/currentPageId',
      webviewEnabled: 'pages/webviewEnabled',
      pageCanUseApi: 'pages/pageCanUseApi',
      commentTemplates: 'reply/comment_templates',
      replyTemplates: 'reply/templates',
      accounts: 'reply/accounts',
    }),
    maxComments() {
      return this.$store.getters['pages/currentPage']?.max_comments ?? 1
    },
    isCommentAlogEnabled() {
      return this.$store.getters['pages/currentPage']?.comment_algo_enable ?? 0
    },
    defaultAccounts() {
      return this.accounts.filter(account => !account.is_multi)
    },
    multiAccounts() {
      return this.accounts.filter(account => !!account.is_multi)
    },
    currentPage() {
      return this.$store.getters['pages/currentPage'] ?? {}
    },
    maxAiLimitForReply() {
      return Math.max(0, Number(this.currentPage.ai_reply_limit ?? 0) - Number(this.currentPage.ai_used ?? 0))
    },
  },
  watch: {
    pageId() {
      this.fetchDependencies()
    },
    templateId(id) {
      this.useTemplate(id)
    },
  },
  async mounted() {
    this.changeTypeReply = false
    await this.fetchDependencies()
    if (this.edit) {
      await this.fetchReply()
    } else {
      this.ensureAiLimitWithinRemaining()
      this.fetch = false
    }
  },
  methods: {
    async useTemplate(id) {
      const template = this.replyTemplates.find(t => t.id === id)
      if (template) {
        this.$store.commit('reply/SET_FORM', template)
        await this.setMessage()
      }
    },
    async fetchReply() {
      const { id } = this.$route.params
      await (this.isTemplate ? ReplyApi.findTemplate(id) : ReplyApi.find(id)).then(async res => {
        this.$store.commit('reply/SET_FORM', res.reply)
        await this.setMessage()
      }).catch(e => { this.fetchError = e; this.fetch = false })
    },
    async fetchDependencies() {
      await this.$store.dispatch('reply/GET_DEPENDENCIES').catch(e => { this.error = e })
      if (this.webviewEnabled) {
        await this.$store.dispatch('webview/GET_TEMPLATES').catch(e => { this.error = e })
      }
    },
    setMessage() {
      // eslint-disable-next-line default-case
      switch (this.form.message_type) {
        case 'text': {
          this.$store.commit('reply/SET_FORM', { message: this.form.reply_message?.text ?? '' })
          break
        }
        case 'buttons':
        {
          this.$store.commit('reply/SET_FORM', {
            message: this.form.reply_message?.attachment?.payload?.text ?? '',
            buttons: this.form.reply_message?.attachment?.payload?.buttons ?? [],
          })
          this.typTabIndex = 1
          break
        }
        case 'generic':
        {
          this.$store.commit('reply/SET_FORM', {
            cards: this.form.reply_message?.attachment?.payload?.elements ?? [],
          })
          this.typTabIndex = 2
          break
        }
        case 'image':
          this.$store.commit('reply/SET_FORM', {
            image: this.form.reply_message?.attachment?.payload?.url ?? '',
          })
          this.typTabIndex = 3
          break
      }
      if (this.form?.stop_at) {
        this.schedule = true
      }
      this.ensureAiLimitWithinRemaining()
      this.fetch = false
    },
    ensureAiLimitWithinRemaining() {
      const max = this.maxAiLimitForReply
      const current = this.form?.ai_limit
      if (current === null || current === undefined || current === '') {
        this.$store.commit('reply/SET_FORM', { ai_limit: max })
        return
      }
      const normalized = Math.max(0, parseInt(String(current), 10) || 0)
      if (normalized > max) {
        this.$store.commit('reply/SET_FORM', { ai_limit: max })
      }
    },
    async submitReply() {
      this.error = null
      if (!this.form.name) {
        this.error = 'اسم النموذج مطلوب'
        this.$notifyError('اسم النموذج مطلوب')
        return
      }
      if (!this.form.message_enabled && !this.form.comment_enabled) {
        this.error = 'يجب تفعيل الرسالة او التعليق'
        this.$notifyError('يجب تفعيل الرسالة او التعليق')
        return
      }
      try {
        let isValid = true
        if (this.schedule) {
          if (this.$refs?.schedule?.$refs?.form) {
            isValid = await this.$refs.schedule.$refs.form.validate()
          }
        }
        if (this.form.message_enabled) {
          await this.$refs?.replyTypes?.$refs[this.form.message_type][0].submit()
          if (!isValid) throw new Error(this.$tc('errors.field-invalid', 1))
        }

        const form = {
          name: this.form.name,
          id: this.form?.id,
          message_enabled: this.form.message_enabled,
          comment_enabled: this.form.comment_enabled ? 'yes' : 'no',
          message_type: this.form.message_type,
          post_description: this.form.post_description,
          post_thumb: this.form.post_thumb,
          page_id: this.pageId,
          save: this.save,
          post_id: this.$route.params.id,
          reply_type: 'generic',
          comment_template_id: this.form.comment_template_id,
          comment_number: this.form.comment_number,
          include_url: this.form.include_url,
          post_url: this.form.post_url,
          multiple_enabled: this.form.enable_multiple,
          delay: this.form.delay ?? 5,
          auto_like: this.form.auto_like ?? false,
        }
        form.reply_message = this.getReplyMessage()
        if (this.schedule && !this.isTemplate) {
          form.stop_at = this.form.stop_at
          form.schedule = this.form.schedule
        }
        if (this.isCommentAlogEnabled && this.form?.account_id) {
          form.account_id = this.form.account_id
        }
        if (this.isCommentAlogEnabled && (this.maxComments > 1) && this.form?.multi_account_id) {
          form.multi_account_id = this.form.multi_account_id
        }
        // Always send for post replies so the API persists them (do not gate on pageCanUseApi; UI still does).
        if (!this.isTemplate) {
          form.ai_enabled = !!(this.form.comment_enabled && this.form.ai_enabled)
          form.ai_post_description = this.form.comment_enabled
            ? String(this.form.ai_post_description ?? '')
            : ''
          form.ai_limit = Math.max(0, parseInt(String(this.form.ai_limit ?? 100), 10) || 0)
        }
        if (this.isTemplate && this.edit) form.id = this.$route.params?.id
        const finalForm = FormsService.convertObjectToFormData(form)
        if (this.form.image) finalForm.append('file', this.form.image)
        this.loading = true
        await (this.isTemplate ? ReplyApi.addTemplate(finalForm) : ReplyApi.addReply(finalForm)).then(res => {
          if (res.error === 0) {
            this.$notifySuccess(res.message ?? 'Added successfully')
            this.$store.commit('reply/SET_DEFAULT_FORM')
            this.$router.push({ name: this.isTemplate ? 'owner:reply-templates' : 'owner:reports' })
          }
        }).catch(e => { this.error = e })
          .finally(() => { this.loading = false })
      } catch (e) {
        this.error = e
      }
    },
    getReplyMessage() {
      switch (this.form.message_type) {
        case 'text': return { text: this.form.message }
        case 'buttons': return {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'button',
              text: this.form.message,
              buttons: this.form.buttons,
            },
          },
        }
        case 'generic': return {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'horizontal',
              elements: this.form.cards,
            },
          },
        }
        case 'image': return {
          attachment: {
            type: 'image',
            payload: {
              is_reusable: true,
              url: '',
            },
          },
        }
        default: return false
      }
    },
    removeFilter(list, index) {
      this.form.filter_messages.splice(index, 1)
    },
    change() {
      this.changeTypeReply = true
    },
  },
}
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 360px){
  .reply-types{
    padding: 0;
  }
  .pt-xs{
    padding-top: 10px;
  }
}
.comment-number-container{
  border: 2px dashed gainsboro;
  padding: 10px;
}
</style>
